import { useState, useEffect, useCallback } from 'react';
import { Search, Mail, Users, TrendingUp, UserMinus, Download, RefreshCw } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

const CARD = "bg-white/[0.04] border border-white/[0.07] rounded-2xl backdrop-blur-sm";
const INPUT = "bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-primary/50 transition-all";
const BTN_PRIMARY = "inline-flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-white rounded-lg px-3.5 py-2 text-sm font-medium transition-all";
const BTN_SECONDARY = "inline-flex items-center gap-1.5 bg-white/[0.06] hover:bg-white/[0.09] border border-white/[0.1] text-white/70 rounded-lg px-3.5 py-2 text-sm font-medium transition-all";

interface Subscriber {
  id: string;
  email: string;
  source: string;
  campaignId?: string;
  isActive: boolean;
  joinedAt: string;
  unsubscribedAt?: string;
}

interface SubscriberStats {
  total: number;
  active: number;
  recent: number;
  unsubscribed: number;
  sources: { _id: string; count: number }[];
}

const SOURCE_LABELS: Record<string, string> = {
  landing_page: 'Landing Page',
  blog_listing: 'Blog Listing',
  blog_post: 'Blog Post',
  website: 'Website',
};

function SubscribersList() {
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [stats, setStats] = useState<SubscriberStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'true' | 'false' | 'all'>('true');
  const [sourceFilter, setSourceFilter] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: '100',
        subscribed: filter,
        ...(sourceFilter ? { source: sourceFilter } : {}),
      });

      const [subRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/subscribers?${params}`),
        fetch(`${API_URL}/api/admin/subscribers/stats`),
      ]);

      const subData = await subRes.json();
      const statsData = await statsRes.json();

      if (subData.success) setSubscribers(subData.subscribers);
      if (statsData.success) setStats(statsData.stats);
    } catch (err) {
      console.error('Failed to fetch subscribers:', err);
    } finally {
      setLoading(false);
    }
  }, [filter, sourceFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExport = () => {
    window.open(`${API_URL}/api/admin/subscribers/export`, '_blank');
  };

  const filtered = subscribers.filter(s =>
    s.email.toLowerCase().includes(search.toLowerCase()) ||
    (s.source || '').toLowerCase().includes(search.toLowerCase())
  );

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  const statCards = stats ? [
    { icon: <Users size={18} />, label: 'Total', value: stats.total, color: '#007AFF' },
    { icon: <Mail size={18} />, label: 'Active', value: stats.active, color: '#34C759' },
    { icon: <TrendingUp size={18} />, label: 'Last 30 days', value: stats.recent, color: '#FF9F0A' },
    { icon: <UserMinus size={18} />, label: 'Unsubscribed', value: stats.unsubscribed, color: '#FF453A' },
  ] : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white">Subscribers</h1>
          <p className="text-white/40 text-sm mt-0.5">Email list from blog and landing page CTAs</p>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchData} className={BTN_SECONDARY}>
            <RefreshCw size={14} />Refresh
          </button>
          <button onClick={handleExport} className={BTN_PRIMARY}>
            <Download size={14} />Export CSV
          </button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card, i) => (
          <div key={i} className={`${CARD} p-4`}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${card.color}20`, color: card.color }}>
                {card.icon}
              </div>
              <span className="text-white/40 text-xs">{card.label}</span>
            </div>
            <p className="text-2xl font-bold text-white">{card.value.toLocaleString()}</p>
          </div>
        ))}
      </div>

      {/* Source Breakdown */}
      {stats && stats.sources.length > 0 && (
        <div className={`${CARD} p-5`}>
          <h3 className="text-sm font-semibold text-white mb-3">Source Breakdown</h3>
          <div className="flex flex-wrap gap-2">
            {stats.sources.map((s) => (
              <button
                key={s._id}
                onClick={() => setSourceFilter(sourceFilter === s._id ? '' : s._id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                  sourceFilter === s._id
                    ? 'bg-primary/20 text-primary border-primary/40'
                    : 'bg-white/[0.04] text-white/60 border-white/[0.08] hover:bg-white/[0.07]'
                }`}
              >
                {SOURCE_LABELS[s._id] || s._id}
                <span className="bg-white/10 rounded px-1">{s.count}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filters + Table */}
      <div className={`${CARD} p-5`}>
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
            <input
              className={`${INPUT} w-full pl-8`}
              placeholder="Search by email or source..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div className="flex gap-1 bg-white/[0.04] border border-white/[0.07] rounded-lg p-1">
            {(['true', 'false', 'all'] as const).map(v => (
              <button
                key={v}
                onClick={() => setFilter(v)}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${
                  filter === v ? 'bg-primary text-white' : 'text-white/40 hover:text-white/70'
                }`}
              >
                {v === 'true' ? 'Active' : v === 'false' ? 'Unsub' : 'All'}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-6 h-6 border-2 border-white/10 border-t-primary rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-white/30 text-sm text-center py-12">No subscribers found</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  <th className="text-left text-white/30 text-xs font-medium pb-3 pr-4">Email</th>
                  <th className="text-left text-white/30 text-xs font-medium pb-3 pr-4">Source</th>
                  <th className="text-left text-white/30 text-xs font-medium pb-3 pr-4">Joined</th>
                  <th className="text-left text-white/30 text-xs font-medium pb-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.04]">
                {filtered.map((sub) => (
                  <tr key={sub.id} className="hover:bg-white/[0.02] transition-colors">
                    <td className="py-3 pr-4 text-white font-medium">{sub.email}</td>
                    <td className="py-3 pr-4">
                      <span className="text-white/50 text-xs bg-white/[0.06] border border-white/[0.1] px-2 py-0.5 rounded">
                        {SOURCE_LABELS[sub.source] || sub.source || '—'}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-white/40 text-xs">{formatDate(sub.joinedAt)}</td>
                    <td className="py-3">
                      {sub.isActive ? (
                        <span className="text-xs font-medium text-green bg-green/15 border border-green/30 px-2 py-0.5 rounded">Active</span>
                      ) : (
                        <span className="text-xs font-medium text-red bg-red/15 border border-red/30 px-2 py-0.5 rounded">Unsubscribed</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-white/20 text-xs mt-4">{filtered.length} subscriber{filtered.length !== 1 ? 's' : ''} shown</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default SubscribersList;
